import SvgError from '@wirechunk/icons/400/20/outlined/error.js';
import { adminPaths } from '@wirechunk/lib/admin-paths.ts';
import { visualPreviewPath } from '@wirechunk/lib/mixer/visual-preview.ts';
import type { PublicSiteLayoutForPathQuery } from '@wirechunk/lib/shared-queries/public-site-layout-for-path-query.generated.ts';
import { PublicSiteLayoutForPathDocument } from '@wirechunk/lib/shared-queries/public-site-layout-for-path-query.generated.ts';
import type { PublicSitePageQuery } from '@wirechunk/lib/shared-queries/public-site-page-query.generated.ts';
import { PublicSitePageDocument } from '@wirechunk/lib/shared-queries/public-site-page-query.generated.ts';
import type { ContextData } from '@wirechunk/schemas/context-data/context-data';
import {
  apolloClient,
  Callout,
  createBrowserRouter,
  PageContext,
  ParseAndRenderComponents,
  ParseAndRenderPage,
  PropsContext,
  ProvideConditionalLogicData,
  useLoaderData,
  ViewMode,
  type RouteObject,
} from '@wirechunk/ui';
import type { DesignerObjectType } from '@wirechunk/ui/api';
import { trim } from 'lodash-es';
import type { FunctionComponent } from 'react';
import { lazy, Suspense, useMemo } from 'react';
import { CurrentSiteProvider } from './components/current-site-provider.tsx';
import { DialogProvider } from './components/dialog-provider.tsx';
import { ErrorBoundary } from './components/error-boundary.tsx';
import { NotFound, notFoundTitle } from './components/not-found.tsx';
import { RethrowErrorBoundary } from './components/rethrow-error-boundary.tsx';
import { ScrollToTop } from './components/scroll-to-top.tsx';
import { visualPreviewObjectType } from './initial-search-params.ts';
import { tryParseObject } from './util/json.ts';

const { admin, siteId, embedDomain, sitesSubscription } = window.wirechunk;

const LazyRenderIframeForm = lazy(() => import('./components/admin/embed-iframe-form.tsx'));
const LazyVisualPreview = lazy(() => import('./components/visual-preview/visual-preview.tsx'));

// This replaces the entire page.
const NoActiveSubscription: FunctionComponent = () => (
  <Callout.Root color="red" m="3" size="3">
    <Callout.Icon>
      <SvgError />
    </Callout.Icon>
    <Callout.Text>
      This site does not have an active hosting subscription. If you own this site, please activate
      your subscription.
    </Callout.Text>
  </Callout.Root>
);

// This overlays the page.
const PastDueSubscription: FunctionComponent = () => (
  <Callout.Root
    m="3"
    size="3"
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      // The default background is semi-transparent. Also --red-9 is more prominent.
      backgroundColor: 'var(--red-9)',
      // The Header component has a z-index of 1000. We must overlay it.
      zIndex: 1002,
      color: '#fff',
    }}
  >
    <Callout.Icon>
      <SvgError />
    </Callout.Icon>
    <Callout.Text weight="medium">
      This site’s hosting subscription is past due. If you own this site, please reactivate your
      subscription.
    </Callout.Text>
  </Callout.Root>
);

type PageRouteData = {
  page: NonNullable<PublicSitePageQuery['publicSite']>['page'];
  layout: NonNullable<PublicSiteLayoutForPathQuery['publicSite']>['layoutForPath'];
};

const PageRoute: FunctionComponent = () => {
  const { page, layout } = useLoaderData<PageRouteData>();
  const pageContext = useMemo<PageContext>(
    () =>
      page
        ? {
            viewMode: ViewMode.Live,
            page,
            children: (
              <ErrorBoundary>
                <Suspense>
                  <ParseAndRenderPage
                    // key is needed so that state is reset if an error boundary is hit.
                    key={page.id}
                    componentsJSON={page.components}
                    bodyStylesJSON={page.bodyStyles}
                  />
                </Suspense>
              </ErrorBoundary>
            ),
          }
        : {
            viewMode: ViewMode.NotFound,
            children: <NotFound noTitle />,
          },
    [page],
  );
  const propsContext = useMemo<ContextData>(
    () => (page?.props ? (tryParseObject(page.props) as ContextData) : {}),
    [page],
  );
  const metaTitle = page ? page.metaTitle || page.title : notFoundTitle;

  return (
    <CurrentSiteProvider>
      <title>{metaTitle}</title>
      {!!page?.metaDescription && <meta name="description" content={page.metaDescription} />}
      {!!page?.metaImageUrl && <meta property="og:image" content={page.metaImageUrl} />}
      {!!page?.metaImageUrl && <meta name="twitter:image" content={page.metaImageUrl} />}
      <meta property="og:title" content={metaTitle} />
      <meta name="twitter:title" content={metaTitle} />
      {!!page?.metaDescription && <meta property="og:description" content={page.metaDescription} />}
      {!!page?.metaDescription && (
        <meta name="twitter:description" content={page.metaDescription} />
      )}
      {!!page?.metaRobots.length && <meta name="robots" content={page.metaRobots.join(', ')} />}
      {sitesSubscription === 'PastDue' && <PastDueSubscription />}
      <PageContext value={pageContext}>
        <PropsContext value={propsContext}>
          <ProvideConditionalLogicData props={propsContext}>
            <ScrollToTop />
            <DialogProvider>
              {layout ? (
                <ParseAndRenderComponents
                  // key is needed so that state is reset if an error boundary is hit.
                  key={layout.id + (page?.id ?? '')}
                  componentsJSON={layout.components}
                />
              ) : (
                pageContext.children
              )}
            </DialogProvider>
          </ProvideConditionalLogicData>
        </PropsContext>
      </PageContext>
    </CurrentSiteProvider>
  );
};

let dashboardPatched = false;

// The Dialog provider needs to be rendered within the RouterProvider so that anything that's rendered within the
// dialog has access to the router context. Note that toasts do not have access to the router.
const routes: RouteObject[] = [];
if (visualPreviewObjectType) {
  // The following visual designer objects can be previewed on any path.
  const previewObjectTypesOnCustomPath: DesignerObjectType[] = ['Page', 'PageTemplate'];

  // TODO: Support overriding pages on the site with page templates to test navigating around a site.
  // TODO: Also support optionally enabling a layout (users may want to preview with or without a layout).
  routes.push({
    path: previewObjectTypesOnCustomPath.includes(visualPreviewObjectType)
      ? window.location.pathname
      : visualPreviewPath,
    element: (
      <CurrentSiteProvider>
        <LazyVisualPreview />
      </CurrentSiteProvider>
    ),
    ErrorBoundary: RethrowErrorBoundary,
  });
}
if (window.location.host === embedDomain) {
  routes.push({
    path: '*',
    element: <LazyRenderIframeForm />,
    ErrorBoundary: RethrowErrorBoundary,
  });
} else {
  routes.push({
    path: '*',
    Component: sitesSubscription ? PageRoute : NoActiveSubscription,
    loader: async ({ params }): Promise<PageRouteData> => {
      // Note that, without trimming slashes, the path param can have leading or trailing slashes.
      const path = trim(params['*'] as string, '/');
      // This is the only way to avoid the query when the page is in the cache.
      const cachedPageData = apolloClient.readQuery<PublicSitePageQuery>({
        query: PublicSitePageDocument,
        variables: { siteId, path },
      });
      if (cachedPageData) {
        // Notice, having cached page data does not mean the page actually exists.
        return {
          page: cachedPageData.publicSite?.page,
          layout: cachedPageData.publicSite?.page
            ? cachedPageData.publicSite.page.resolvedLayout
            : apolloClient.readQuery<PublicSiteLayoutForPathQuery>({
                query: PublicSiteLayoutForPathDocument,
                variables: { siteId, path },
              })?.publicSite?.layoutForPath,
        };
      }
      const { data: pageData } = await apolloClient.query({
        query: PublicSitePageDocument,
        variables: { siteId, path },
      });
      const page = pageData.publicSite?.page;
      if (page) {
        return {
          page,
          layout: page.resolvedLayout,
        };
      }
      // We're going to fill the cache with the resolved layout for the path. So next time this path is visited,
      // we can just read the layout from the cache (as we do in the code above).
      const { data: layoutData } = await apolloClient.query({
        query: PublicSiteLayoutForPathDocument,
        variables: { siteId, path },
      });
      return {
        page: null,
        layout: layoutData.publicSite?.layoutForPath,
      };
    },
    ErrorBoundary: RethrowErrorBoundary,
  });
}

export const router = createBrowserRouter(routes, {
  patchRoutesOnNavigation: admin
    ? async ({ path, patch }) => {
        if (!dashboardPatched && adminPaths.some((adminPath) => path.startsWith(adminPath))) {
          const { adminRoutes } = await import('./admin-routes.tsx');
          patch(null, adminRoutes);
          dashboardPatched = true;
        }
      }
    : undefined,
});
